
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useLeads, useActivities, useActivityMutations } from '../hooks/useCRM';
import { getAudit } from '../utils/crmApi';
import ActivityTimeline from '../components/ActivityTimeline';
import { useToast } from '../components/Toast';
import { useConfirm } from '../hooks/useConfirm';
import '../crm.css';
import { leadPath } from '../utils/crmEnv';

/* ── helpers ──────────────────────────────────────────────────────────── */

const getEpoch = (v) => {
    if (!v && v !== 0) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
};

const latestActivityTime = (activities) => {
    if (!activities || activities.length === 0) return 0;
    let max = 0;
    for (const a of activities) {
        const t = getEpoch(a.dueAt) ?? getEpoch(a.completedAt) ?? getEpoch(a.updatedAt) ?? getEpoch(a.createdAt) ?? 0;
        if (t > max) max = t;
    }
    return max;
};

const formatRelativeShort = (epoch) => {
    if (!epoch) return '';
    const diff = Date.now() - epoch;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(epoch).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatFullDate = (epoch) => {
    if (!epoch) return '';
    return new Date(epoch).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
    });
};

const statusColors = {
    new: '#3b82f6',
    contacted: '#8b5cf6',
    qualified: '#10b981',
    unqualified: '#ef4444',
    converted: '#059669',
    lost: '#6b7280',
    hot: '#ef4444',
    warm: '#f59e0b',
    cold: '#3b82f6',
};

/* ── Audit event → timeline item icons / colors ──────────────────────── */

const auditActionMeta = {
    create: { icon: '✦', color: '#10b981', label: 'Created' },
    update: { icon: '✎', color: '#8b5cf6', label: 'Updated' },
    delete: { icon: '✕', color: '#ef4444', label: 'Deleted' },
    merge: { icon: '⇋', color: '#f59e0b', label: 'Merged' },
    import: { icon: '↓', color: '#06b6d4', label: 'Imported' },
    export: { icon: '↑', color: '#64748b', label: 'Exported' },
    upload: { icon: '⬆', color: '#3b82f6', label: 'Uploaded' },
    download: { icon: '⬇', color: '#64748b', label: 'Downloaded' },
};

const auditEntityIcons = {
    lead: '👤',
    activity: '📋',
    deal: '💰',
    contact: '📇',
    property: '🏠',
    file: '📎',
};

/* ── component ────────────────────────────────────────────────────────── */

const ContactsPage = () => {
    const history = useHistory();
    const isLightMode = typeof document !== 'undefined' && !document.body.classList.contains('dark-mode');

    // Data hooks
    const { leads, loading: leadsLoading, error: leadsError, refetch: refetchLeads } = useLeads({ pageSize: 200 });
    const { activities, loading: activitiesLoading, refetch: refetchActivities } = useActivities({ pageSize: 500 });
    const { complete, update: updateActivity } = useActivityMutations();
    const { showSuccess, showError } = useToast();
    const { confirm, ConfirmDialog } = useConfirm();

    // State
    const [selectedLeadId, setSelectedLeadId] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [auditEvents, setAuditEvents] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [timelineTab, setTimelineTab] = useState('all'); // all | activities | changes

    // Group activities by lead (entityId)
    const activitiesByLead = useMemo(() => {
        const map = {};
        (activities || []).forEach((a) => {
            const leadId = a.leadId || (a.entityType === 'lead' ? a.entityId : null);
            if (!leadId) return;
            if (!map[leadId]) map[leadId] = [];
            map[leadId].push(a);
        });
        return map;
    }, [activities]);

    // Filtered & sorted leads
    const filteredLeads = useMemo(() => {
        let list = leads || [];
        if (searchInput.trim()) {
            const q = searchInput.toLowerCase();
            list = list.filter((l) => {
                const name = `${l.firstName || ''} ${l.lastName || ''} ${l.name || ''}`.toLowerCase();
                const phone = (l.phone || l.mobile || '').toLowerCase();
                const email = (l.email || '').toLowerCase();
                return name.includes(q) || phone.includes(q) || email.includes(q);
            });
        }
        if (statusFilter) {
            list = list.filter((l) => (l.status || l.lifecycleStage || '') === statusFilter);
        }
        return [...list].sort((a, b) => {
            const ta = latestActivityTime(activitiesByLead[a.id] || []);
            const tb = latestActivityTime(activitiesByLead[b.id] || []);
            return tb - ta;
        });
    }, [leads, searchInput, statusFilter, activitiesByLead]);

    // Auto-select first lead
    useEffect(() => {
        if (!selectedLeadId && filteredLeads.length > 0) {
            setSelectedLeadId(filteredLeads[0].id);
        }
    }, [filteredLeads, selectedLeadId]);

    // Fetch audit events when lead changes
    useEffect(() => {
        if (!selectedLeadId) {
            setAuditEvents([]);
            return;
        }
        let cancelled = false;
        const fetchAudit = async () => {
            setAuditLoading(true);
            try {
                const result = await getAudit({ entityId: selectedLeadId, limit: 200 });
                if (!cancelled) setAuditEvents(result.data || []);
            } catch (err) {
                console.error('Audit fetch failed:', err);
                if (!cancelled) setAuditEvents([]);
            } finally {
                if (!cancelled) setAuditLoading(false);
            }
        };
        fetchAudit();
        return () => { cancelled = true; };
    }, [selectedLeadId]);

    // Selected lead data
    const selectedLead = useMemo(
        () => (leads || []).find((l) => l.id === selectedLeadId) || null,
        [leads, selectedLeadId],
    );

    const selectedActivities = useMemo(
        () => activitiesByLead[selectedLeadId] || [],
        [activitiesByLead, selectedLeadId],
    );

    // Build unified timeline: merge activities + audit events
    const unifiedTimeline = useMemo(() => {
        const items = [];
        const activityIds = new Set();

        // 1. Add activities as primary timeline items
        selectedActivities.forEach((a) => {
            activityIds.add(a.id);
            items.push({
                _kind: 'activity',
                _time: getEpoch(a.dueAt) ?? getEpoch(a.completedAt) ?? getEpoch(a.createdAt) ?? 0,
                ...a,
            });
        });

        // 2. Add audit events that aren't just activity-related (or are non-activity entities)
        auditEvents.forEach((evt) => {
            // Skip audit events about an activity we already show
            if (evt.entityType === 'activity' && activityIds.has(evt.entityId)) return;
            items.push({
                _kind: 'audit',
                _time: getEpoch(evt.at) ?? 0,
                _audit: evt,
                id: `audit-${evt.id}`,
            });
        });

        // 3. Sort by time, newest first
        items.sort((a, b) => b._time - a._time);
        return items;
    }, [selectedActivities, auditEvents]);

    // Filtered timeline based on tab
    const filteredTimeline = useMemo(() => {
        if (timelineTab === 'activities') return unifiedTimeline.filter((i) => i._kind === 'activity');
        if (timelineTab === 'changes') return unifiedTimeline.filter((i) => i._kind === 'audit');
        return unifiedTimeline;
    }, [unifiedTimeline, timelineTab]);

    // Activity stats
    const activityStats = useMemo(() => {
        const items = selectedActivities;
        let total = items.length;
        let completed = 0;
        let overdue = 0;
        let pending = 0;
        const now = Date.now();
        items.forEach((a) => {
            if (a.status === 'completed' || a.completed) {
                completed++;
            } else {
                const due = getEpoch(a.dueAt) ?? getEpoch(a.dueDate);
                if (due && due < now) overdue++;
                else pending++;
            }
        });
        return { total, completed, overdue, pending, auditCount: auditEvents.length };
    }, [selectedActivities, auditEvents]);

    // Handlers
    const handleComplete = useCallback(async (activity) => {
        try {
            await complete(activity.id);
            showSuccess('Activity marked as done');
            refetchActivities();
        } catch {
            showError('Failed to complete activity');
        }
    }, [complete, refetchActivities, showSuccess, showError]);

    const handleOutcomeChange = useCallback(async (activity, outcome) => {
        try {
            await updateActivity(activity.id, { outcome });
            refetchActivities();
        } catch {
            showError('Failed to update outcome');
        }
    }, [updateActivity, refetchActivities, showError]);

    const handlePrimaryAction = useCallback((activity, channelKey) => {
        const phone = selectedLead?.phone || selectedLead?.mobile || '';
        const email = selectedLead?.email || '';
        if (channelKey === 'call' && phone) window.open(`tel:${phone}`, '_self');
        else if (channelKey === 'whatsapp' && phone) window.open(`https://wa.me/${phone.replace(/[^\d]/g, '')}`, '_blank', 'noopener,noreferrer');
        else if (channelKey === 'email' && email) window.open(`mailto:${email}`, '_self');
    }, [selectedLead]);

    const handleEdit = useCallback((activity) => {
        if (selectedLeadId) history.push(`${leadPath(selectedLeadId)}?tab=activities`);
    }, [selectedLeadId, history]);

    const refetchAll = useCallback(() => {
        refetchLeads();
        refetchActivities();
    }, [refetchLeads, refetchActivities]);

    // Get unique statuses for filter
    const statusOptions = useMemo(() => {
        const set = new Set();
        (leads || []).forEach((l) => {
            const s = l.status || l.lifecycleStage;
            if (s) set.add(s);
        });
        return Array.from(set).sort();
    }, [leads]);

    const isLoading = leadsLoading || activitiesLoading;

    const getLeadName = (lead) => {
        if (lead.name) return lead.name;
        return `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Unnamed Lead';
    };

    const getLeadInitials = (lead) => {
        const name = getLeadName(lead);
        const parts = name.split(' ').filter(Boolean);
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return (parts[0] || 'U').charAt(0).toUpperCase();
    };

    /* ── Render an audit event row ─────────────────────────────────────── */
    const renderAuditItem = (evt) => {
        const meta = auditActionMeta[evt.action] || { icon: '•', color: '#64748b', label: evt.action };
        const entityIcon = auditEntityIcons[evt.entityType] || '📋';
        const time = getEpoch(evt.at);
        const changes = evt.changes || [];

        return (
            <div style={{
                display: 'flex',
                gap: '12px',
                padding: '12px 0',
                borderBottom: '1px solid var(--crm-gray-100)',
                minWidth: 0,
            }}>
                {/* Timeline dot */}
                <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: meta.color + '18',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    flexShrink: 0,
                    color: isLightMode ? meta.color : undefined,
                    border: `2px solid ${meta.color}40`,
                }}>
                    {entityIcon}
                </div>
                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: '8px',
                    }}>
                        <div>
                            <span style={{
                                fontSize: '13px',
                                fontWeight: '500',
                                color: 'var(--crm-gray-900)',
                            }}>
                                {evt.summary}
                            </span>
                            <span style={{
                                display: 'inline-block',
                                marginLeft: '8px',
                                fontSize: '10px',
                                padding: '1px 6px',
                                borderRadius: '3px',
                                background: meta.color + '20',
                                color: meta.color,
                                fontWeight: '600',
                                textTransform: 'capitalize',
                                verticalAlign: 'middle',
                            }}>
                                {meta.label}
                            </span>
                        </div>
                        <span style={{
                            fontSize: '11px',
                            color: 'var(--crm-gray-500)',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                        }}>
                            {formatFullDate(time)}
                        </span>
                    </div>
                    {/* Actor */}
                    {evt.actorName && (
                        <div style={{ fontSize: '11px', color: 'var(--crm-gray-500)', marginTop: '2px' }}>
                            by {evt.actorName}
                        </div>
                    )}
                    {/* Changes */}
                    {changes.length > 0 && (
                        <div style={{
                            marginTop: '6px',
                            padding: '6px 10px',
                            background: 'var(--crm-gray-100)',
                            border: '1px solid var(--crm-gray-200)',
                            borderRadius: '6px',
                            fontSize: '12px',
                        }}>
                            {changes.map((c, i) => (
                                <div key={i} style={{
                                    display: 'flex',
                                    gap: '4px',
                                    flexWrap: 'wrap',
                                    minWidth: 0,
                                    padding: '2px 0',
                                    color: 'var(--crm-gray-700)',
                                }}>
                                    <span style={{ fontWeight: '500', color: 'var(--crm-gray-600)' }}>
                                        {c.field}:
                                    </span>
                                    {c.from != null && (
                                        <span style={{
                                            textDecoration: 'line-through',
                                            color: '#64748b',
                                        }}>
                                            {String(c.from)}
                                        </span>
                                    )}
                                    <span style={{ color: '#64748b' }}>→</span>
                                    <span style={{ fontWeight: '500' }}>{String(c.to)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {/* Meta info */}
                    {evt.meta && (
                        <div style={{
                            marginTop: '4px',
                            fontSize: '11px',
                            color: 'var(--crm-gray-500)',
                        }}>
                            {evt.meta.location && <span>📍 {evt.meta.location} </span>}
                            {evt.meta.propertyTitle && <span>🏠 {evt.meta.propertyTitle} </span>}
                            {evt.meta.dealTitle && <span>💰 {evt.meta.dealTitle} </span>}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="crm-container">
                {/* Header */}
                <div className="crm-page-header">
                    <h1 className="crm-page-title">
                        Contacts
                        <span className="count-badge">{(leads || []).length}</span>
                    </h1>
                </div>

                {isLoading ? (
                    <div className="crm-loading">
                        <div className="crm-spinner"></div>
                    </div>
                ) : leadsError ? (
                    <div className="crm-empty-state">
                        <p style={{ color: 'var(--crm-danger)' }}>{leadsError}</p>
                        <button className="crm-btn crm-btn-primary" onClick={refetchAll}>Retry</button>
                    </div>
                ) : (!leads || leads.length === 0) ? (
                    <div className="crm-empty-state">
                        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.3 }}>
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 6v6l4 2" />
                        </svg>
                        <h3>No leads found</h3>
                        <p>Create leads to see their activity timeline here.</p>
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '340px 1fr',
                        gap: '0',
                        height: 'calc(100vh - 160px)',
                        minHeight: 0,
                        border: '1px solid var(--crm-gray-200)',
                        borderRadius: '8px',
                        overflow: 'hidden',
                    }}>
                        {/* ── Left sidebar: Lead list ── */}
                        <div style={{
                            borderRight: '1px solid var(--crm-gray-200)',
                            display: 'flex',
                            flexDirection: 'column',
                            background: 'var(--crm-bg)',
                            minHeight: 0,
                            overflow: 'hidden',
                        }}>
                            {/* Search & filter */}
                            <div style={{
                                padding: '12px',
                                borderBottom: '1px solid var(--crm-gray-200)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                            }}>
                                <input
                                    type="text"
                                    className="crm-input"
                                    placeholder="Search leads..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    style={{ fontSize: '13px' }}
                                />
                                <select
                                    className="crm-select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    style={{ fontSize: '13px' }}
                                >
                                    <option value="">All Stages</option>
                                    {statusOptions.map((s) => (
                                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Lead list */}
                            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehaviorY: 'contain' }}>
                                {filteredLeads.length === 0 ? (
                                    <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--crm-gray-500)', fontSize: '13px' }}>
                                        No leads match your search.
                                    </div>
                                ) : (
                                    filteredLeads.map((lead) => {
                                        const isSelected = selectedLeadId === lead.id;
                                        const leadActivities = activitiesByLead[lead.id] || [];
                                        const lastTime = latestActivityTime(leadActivities);
                                        const status = lead.status || lead.lifecycleStage || '';
                                        const overdueCount = leadActivities.filter((a) => {
                                            if (a.status === 'completed' || a.completed) return false;
                                            const due = getEpoch(a.dueAt) ?? getEpoch(a.dueDate);
                                            return due && due < Date.now();
                                        }).length;

                                        return (
                                            <div
                                                key={lead.id}
                                                onClick={() => setSelectedLeadId(lead.id)}
                                                style={{
                                                    padding: '12px 16px',
                                                    cursor: 'pointer',
                                                    borderBottom: '1px solid var(--crm-gray-200)',
                                                    background: isSelected ? 'var(--crm-gray-100)' : 'transparent',
                                                    borderLeft: isSelected ? '3px solid var(--crm-primary)' : '3px solid transparent',
                                                    transition: 'all 0.15s ease',
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div style={{
                                                        width: '36px', height: '36px', borderRadius: '50%',
                                                        background: statusColors[status] || 'var(--crm-gray-400)',
                                                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '13px', fontWeight: '600', flexShrink: 0,
                                                    }}>
                                                        {getLeadInitials(lead)}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                                            <span style={{ fontWeight: '500', fontSize: '13px', color: 'var(--crm-gray-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {getLeadName(lead)}
                                                            </span>
                                                            <span style={{ fontSize: '11px', color: 'var(--crm-gray-500)', flexShrink: 0, marginLeft: '8px' }}>
                                                                {formatRelativeShort(lastTime)}
                                                            </span>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            {status && (
                                                                <span style={{
                                                                    fontSize: '10px', padding: '1px 6px', borderRadius: '3px',
                                                                    background: (statusColors[status] || 'var(--crm-gray-400)') + '20',
                                                                    color: statusColors[status] || 'var(--crm-gray-600)',
                                                                    fontWeight: '500', textTransform: 'capitalize',
                                                                }}>
                                                                    {status}
                                                                </span>
                                                            )}
                                                            <span style={{ fontSize: '11px', color: 'var(--crm-gray-500)' }}>
                                                                {leadActivities.length} event{leadActivities.length !== 1 ? 's' : ''}
                                                            </span>
                                                            {overdueCount > 0 && (
                                                                <span style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '3px', background: 'var(--crm-danger)', color: '#fff', fontWeight: '600' }}>
                                                                    {overdueCount} overdue
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            <div style={{ padding: '8px 16px', borderTop: '1px solid var(--crm-gray-200)', fontSize: '11px', color: 'var(--crm-gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''}
                            </div>
                        </div>

                        {/* ── Right panel: Unified Timeline ── */}
                        <div style={{ display: 'flex', flexDirection: 'column', background: 'var(--crm-bg)', overflow: 'hidden', minWidth: 0, minHeight: 0 }}>
                            {selectedLead ? (
                                <>
                                    {/* Lead header */}
                                    <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--crm-gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                                                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--crm-gray-900)' }}>
                                                    {getLeadName(selectedLead)}
                                                </h2>
                                                {(selectedLead.status || selectedLead.lifecycleStage) && (
                                                    <span className={`crm-badge crm-badge-${selectedLead.status || selectedLead.lifecycleStage}`}>
                                                        {selectedLead.status || selectedLead.lifecycleStage}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: '12px', color: 'var(--crm-gray-500)', display: 'flex', gap: '16px', flexWrap: 'wrap', minWidth: 0 }}>
                                                {selectedLead.phone && <span>📞 {selectedLead.phone}</span>}
                                                {selectedLead.email && <span>✉ {selectedLead.email}</span>}
                                                {selectedLead.source && <span>Source: {selectedLead.source}</span>}
                                            </div>
                                        </div>
                                        <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => history.push(leadPath(selectedLeadId))}>
                                            Open Lead →
                                        </button>
                                    </div>

                                    {/* Stats bar */}
                                    <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--crm-gray-200)', display: 'flex', gap: '24px', alignItems: 'center' }}>
                                        {[
                                            { label: 'Total Events', value: activityStats.total + activityStats.auditCount, color: 'var(--crm-gray-900)' },
                                            { label: 'Activities', value: activityStats.total, color: 'var(--crm-primary)' },
                                            { label: 'Done', value: activityStats.completed, color: 'var(--crm-success)' },
                                            { label: 'Overdue', value: activityStats.overdue, color: activityStats.overdue > 0 ? 'var(--crm-danger)' : 'var(--crm-gray-900)' },
                                            { label: 'Changes', value: activityStats.auditCount, color: '#8b5cf6' },
                                        ].map((stat) => (
                                            <div key={stat.label} style={{ textAlign: 'center' }}>
                                                <div style={{ fontSize: '18px', fontWeight: '700', color: stat.color }}>{stat.value}</div>
                                                <div style={{ fontSize: '10px', color: 'var(--crm-gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{stat.label}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Timeline tabs */}
                                    <div style={{ padding: '0 24px', borderBottom: '1px solid var(--crm-gray-200)', display: 'flex', gap: '0' }}>
                                        {[
                                            { key: 'all', label: 'All', count: unifiedTimeline.length },
                                            { key: 'activities', label: 'Activities', count: selectedActivities.length },
                                            { key: 'changes', label: 'Changes & Audit', count: auditEvents.length },
                                        ].map((tab) => (
                                            <button
                                                key={tab.key}
                                                type="button"
                                                onClick={() => setTimelineTab(tab.key)}
                                                style={{
                                                    padding: '10px 16px',
                                                    border: 'none',
                                                    borderBottom: timelineTab === tab.key ? '2px solid var(--crm-primary)' : '2px solid transparent',
                                                    background: 'transparent',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: timelineTab === tab.key ? '600' : '400',
                                                    color: timelineTab === tab.key ? 'var(--crm-primary)' : 'var(--crm-gray-600)',
                                                }}
                                            >
                                                {tab.label}
                                                <span style={{
                                                    marginLeft: '6px', fontSize: '11px', padding: '1px 6px',
                                                    borderRadius: '8px', background: 'var(--crm-gray-100)',
                                                    color: 'var(--crm-gray-600)',
                                                }}>
                                                    {tab.count}
                                                </span>
                                            </button>
                                        ))}
                                    </div>

                                    {/* Timeline content */}
                                    <div
                                        style={{
                                            flex: 1,
                                            overflowY: 'auto',
                                            overflowX: isLightMode ? 'hidden' : undefined,
                                            padding: isLightMode ? '16px 28px' : '16px 24px',
                                            minWidth: 0,
                                        }}
                                    >
                                        {auditLoading && (
                                            <div style={{ padding: '12px', textAlign: 'center', color: 'var(--crm-gray-500)', fontSize: '13px' }}>
                                                Loading full timeline...
                                            </div>
                                        )}
                                        {filteredTimeline.length === 0 && !auditLoading ? (
                                            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--crm-gray-500)', fontSize: '13px' }}>
                                                No events found for this lead.
                                            </div>
                                        ) : (
                                            <>
                                                {/* Render mixed timeline: activities via ActivityTimeline, audit events inline */}
                                                {timelineTab === 'activities' ? (
                                                    <ActivityTimeline
                                                        activities={selectedActivities}
                                                        lead={selectedLead}
                                                        onComplete={handleComplete}
                                                        onEdit={handleEdit}
                                                        onPrimaryAction={handlePrimaryAction}
                                                        onOutcomeChange={handleOutcomeChange}
                                                    />
                                                ) : (
                                                    <div>
                                                        {filteredTimeline.map((item) => {
                                                            if (item._kind === 'audit') {
                                                                return (
                                                                    <div key={item.id}>
                                                                        {renderAuditItem(item._audit)}
                                                                    </div>
                                                                );
                                                            }
                                                            // For activity items in "all" view, render inline card
                                                            const a = item;
                                                            const time = a._time;
                                                            const isCompleted = a.status === 'completed' || a.completed;
                                                            const due = getEpoch(a.dueAt) ?? getEpoch(a.dueDate);
                                                            const isOverdue = !isCompleted && due && due < Date.now();
                                                            const typeLabel = (a.type || 'note').replace(/_/g, ' ');

                                                            return (
                                                                <div key={a.id} style={{
                                                                    display: 'flex', gap: '12px', padding: '12px 0',
                                                                    borderBottom: '1px solid var(--crm-gray-100)',
                                                                    minWidth: 0,
                                                                }}>
                                                                    <div style={{
                                                                        width: '32px', height: '32px', borderRadius: '50%',
                                                                        background: isCompleted ? '#10b98118' : isOverdue ? '#ef444418' : '#3b82f618',
                                                                        border: `2px solid ${isCompleted ? '#10b98140' : isOverdue ? '#ef444440' : '#3b82f640'}`,
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        fontSize: '12px', flexShrink: 0, fontWeight: '700',
                                                                        color: isCompleted
                                                                            ? (isLightMode ? '#047857' : '#6ee7b7')
                                                                            : isOverdue
                                                                                ? (isLightMode ? '#b91c1c' : '#fca5a5')
                                                                                : (isLightMode ? '#1d4ed8' : '#93c5fd'),
                                                                    }}>
                                                                        {isCompleted ? '✓' : isOverdue ? '!' : '●'}
                                                                    </div>
                                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                                                            <div>
                                                                                <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--crm-gray-900)' }}>
                                                                                    {a.title || a.description || typeLabel}
                                                                                </span>
                                                                                <span style={{
                                                                                    display: 'inline-block', marginLeft: '8px',
                                                                                    fontSize: '10px', padding: '1px 6px', borderRadius: '3px',
                                                                                    background: isCompleted ? '#10b98120' : isOverdue ? '#ef444420' : '#3b82f620',
                                                                                    color: isCompleted ? '#10b981' : isOverdue ? '#ef4444' : '#3b82f6',
                                                                                    fontWeight: '600', textTransform: 'capitalize',
                                                                                }}>
                                                                                    {typeLabel}
                                                                                </span>
                                                                                {isCompleted && (
                                                                                    <span style={{
                                                                                        display: 'inline-block', marginLeft: '4px',
                                                                                        fontSize: '10px', padding: '1px 6px', borderRadius: '3px',
                                                                                        background: '#10b98120', color: '#10b981', fontWeight: '600',
                                                                                    }}>Done</span>
                                                                                )}
                                                                                {isOverdue && (
                                                                                    <span style={{
                                                                                        display: 'inline-block', marginLeft: '4px',
                                                                                        fontSize: '10px', padding: '1px 6px', borderRadius: '3px',
                                                                                        background: '#ef444420', color: '#ef4444', fontWeight: '600',
                                                                                    }}>Overdue</span>
                                                                                )}
                                                                            </div>
                                                                            <span style={{ fontSize: '11px', color: 'var(--crm-gray-500)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                                                {formatFullDate(time)}
                                                                            </span>
                                                                        </div>
                                                                        {a.description && a.title && (
                                                                            <div style={{ fontSize: '12px', color: 'var(--crm-gray-600)', marginTop: '4px' }}>
                                                                                {a.description}
                                                                            </div>
                                                                        )}
                                                                        {a.outcome && (
                                                                            <div style={{ fontSize: '11px', color: 'var(--crm-gray-500)', marginTop: '2px' }}>
                                                                                Outcome: <span style={{ fontWeight: '500' }}>{a.outcome.replace(/_/g, ' ')}</span>
                                                                            </div>
                                                                        )}
                                                                        {!isCompleted && (
                                                                            <div style={{ marginTop: '6px', display: 'flex', gap: '6px' }}>
                                                                                <button
                                                                                    className="crm-btn crm-btn-secondary crm-btn-sm"
                                                                                    type="button"
                                                                                    style={{ fontSize: '11px', padding: '2px 8px' }}
                                                                                    onClick={(e) => { e.stopPropagation(); handleComplete(a); }}
                                                                                >
                                                                                    ✓ Done
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="crm-empty-state" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ opacity: 0.25, marginBottom: '16px' }}>
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="M12 6v6l4 2" />
                                        </svg>
                                        <h3 style={{ margin: '0 0 4px', color: 'var(--crm-gray-800)' }}>Select a lead</h3>
                                        <p style={{ margin: 0, color: 'var(--crm-gray-500)', fontSize: '13px' }}>
                                            Choose a lead from the list to view their complete timeline.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <ConfirmDialog />
            </div>
        </>
    );
};

export default ContactsPage;

